<template>
  <header class="lhf" ref="hero" id="signup">

    <div class="lhf__bg" aria-hidden="true"></div>

    <!-- Logo: arriba-izquierda en móvil; columna izquierda en desktop -->
    <div class="lhf__logo">
      <router-link to="#">
        <img src="../../../assets/img/logos/logo-hellowish-dark.webp" alt="HelloWish" />
      </router-link>
    </div>

    <!-- Columna contenido: texto + formulario -->
    <div class="lhf__content" ref="scrolltoResponsive">

      <h1 class="lhf__title">
        <slot name="title">{{ title }}</slot>
      </h1>

      <p class="lhf__subtitle" v-if="subtitle">{{ subtitle }}</p>

      <div class="lhf__form" :class="{ 'lhf__form--compact': !showPassword }">
        <InputText
          :label="hideLabels ? '' : $t('landings.hero.yourmail', lang)"
          v-model="email"
          :errorLabel="emailError"
          :placeholder="$t('landings.hero.yourmailplaceholder', lang)"
          ref="heroEmail"
          @focus="emailError = ''"
          @keyup.native="showPassword = true"
        />
        <transition name="lhf-reveal" mode="out-in" appear>
          <div v-if="showPassword" aria-live="polite">
            <InputText
              ref="passwordInput"
              :label="hideLabels ? '' : $t('landings.hero.yourpassword', lang)"
              v-model="password"
              type="password"
              :errorLabel="passwordError"
              :placeholder="$t('landings.hero.yourpasswordplaceholder', lang)"
              @focus="passwordError = ''"
            />
            <div class="lhf__legal">
              <label style="display:inline;">
                <span v-html="$t('signup.acceptTCpart1ImplicitVersion', lang)"></span>
                <router-link
                  :to="$t('routes.confidentialityPolicy', lang)"
                  style="text-decoration:underline;"
                  target="_blank"
                >{{ $t("signup.acceptTCpart2", lang) }}</router-link>.
              </label>
            </div>
          </div>
        </transition>
      </div>

      <div class="lhf__actions">
        <bbButton
          class="button--accent button--block"
          :label="buttonText"
          @click="signUp"
          :loading="loading"
        />
        <p class="lhf__social-proof">Únete a más de 100.000 usuarios felices.</p>
      </div>

      <login-social
        ref="loginSocial"
        :show="true"
        :showFacebook="false"
        :showLabel="true"
        class="login-box__social"
        color="yellow"
        size="large"
        :origin="origin"
        :forcedLang="lang"
        :listType="listType"
        :verticalAlign="true"
        :promoCode="promoCode"
      />
    </div>

    <!-- Columna imagen: abajo en móvil, derecha en desktop -->
    <div class="lhf__cover" :class="{ 'lhf__cover--bleed': coverFullBleed }">
      <slot name="cover"></slot>
    </div>

    <!-- CTA sticky footer (aparece cuando el hero sale del viewport) -->
    <div class="lhf__sticky-bar" v-show="showStickyCta" role="complementary">
      <p class="lhf__sticky-text">{{ stickyText }}</p>
      <button class="lhf__sticky-btn" @click="scrollToHero">
        {{ stickyButtonText || buttonText }}
      </button>
    </div>

  </header>
</template>

<script>
import LoginSocial from "../../../components/ui/login/LoginSocial.vue";
import InputText from "../../../components/forms/InputText.vue";
import bbButton from "../../../components/buttons/bbButton.vue";
const crypto = require("crypto");
import loginMixin from "../../mixins/login.js";

export default {
  name: "LandingHeroFull",
  mixins: [loginMixin],
  components: { LoginSocial, InputText, bbButton },
  props: {
    title:      { type: String, required: true },
    subtitle:   { type: String, default: "" },
    buttonText:       { type: String, default: "Empieza tu lista ahora" },
    stickyButtonText: { type: String, default: "" },
    origin:     { type: String, required: true },
    lang:       { type: String, default: "es-ES" },
    listType:   { type: String, default: "wishlist" },
    promoCode:  { type: String, default: "" },
    stickyText: { type: String, default: "No dejes que tu próximo regalo acabe siendo una devolución." },
    hideSticky:      { type: Boolean, default: false },
    hideLabels:      { type: Boolean, default: false },
    coverFullBleed:  { type: Boolean, default: false },
  },
  data() {
    return {
      email: "",
      password: "",
      loading: false,
      emailError: "",
      passwordError: "",
      showPassword: false,
      scrolledPast: false,
      _io: null,
    };
  },
  computed: {
    isFormValid() {
      return this.email && this.password && this.validateEmail();
    },
    showStickyCta() {
      return this.scrolledPast && !this.hideSticky;
    },
  },
  mounted() {
    const socialEl = this.$refs.loginSocial?.$el;
    if (!socialEl) return;
    this._io = new IntersectionObserver(
      ([entry]) => { this.scrolledPast = !entry.isIntersecting; },
      { threshold: 0 }
    );
    this._io.observe(socialEl);
  },
  beforeDestroy() {
    if (this._io) { try { this._io.disconnect(); } catch (e) {} this._io = null; }
  },
  methods: {
    async signUp() {
      this.email = this.email.trim();
      this.loading = true;
      this.emailError = "";
      this.passwordError = "";

      if (!this.email) { this.emailError = this.$t("landings.hero.emailisneeded", this.lang); }
      if (!this.password) { this.passwordError = this.$t("landings.hero.passwordisneeded", this.lang); }
      if (!this.validateEmail()) {
        this.emailError = this.$t("landings.hero.emailisnotvalid", this.lang);
        this.loading = false;
      }
      if (!this.isFormValid) return;

      let source_url = "";
      try { source_url = localStorage.getItem("source_url"); } catch (e) {}

      try {
        await this.$store.dispatch("registerUser", {
          email: this.email,
          password: this.password,
          signupOrigin: this.origin,
          listType: this.listType,
          referralUrl: source_url,
          language: this.lang,
          promoCode: this.promoCode,
        });
        this.setModalShownCookie();
      } catch (error) {
        if (parseInt(error.response?.status) === 409) {
          this.loading = false;
          this.error = "Este email ya ha sido registrado. ¿Quieres iniciar sesión?";
          await this.$sleep(this.goToLogin, 2000);
          return;
        }
      }

      window.mixpanel?.people?.set?.("Signup Origin", this.origin);
      this.$root?.trackConversion?.({ email: this.email });
      // if (this.listType == "wishlist") return this.login(`/onboarding-0?newuser=true`);
      // else return this.login(`/onboarding/${encodeURIComponent(this.listType)}`);
      return this.login(`/onboarding/${encodeURIComponent(this.listType)}`);
    },

    validateEmail() {
      const email = (this.email || "").toLowerCase();
      const parts = email.split("@");
      if (parts.length !== 2) return false;
      const [left, right] = parts;
      return left.match(/^\w+([.-_+]\w+)*$/) && right.match(/^\w+([.-]\w+)*\.\w{2,10}$/);
    },

    setModalShownCookie() {
      const days = 30;
      const exp = new Date();
      exp.setDate(exp.getDate() + days);
      const hash = crypto.createHash("sha256")
        .update(new Date().getTime().toString() + Math.random().toString())
        .digest("hex");
      document.cookie = `modalShown=${hash}; expires=${exp.toUTCString()}; path=/`;
    },

    async goToLogin() {
      await this.$router.push({ name: "login" });
    },

    scrollToHero() {
      if (window.innerWidth < 992) {
        const el = this.$refs.scrolltoResponsive;
        if (el?.scrollIntoView) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 12;
          window.scrollTo({ top, behavior: "smooth" });
          return;
        }
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
};
</script>

<style scoped>
/* ── Contenedor principal ─────────────────────────────────── */
.lhf {
  min-height: 100svh;
  background: #FFCC00;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 32px 24px 56px;
}
.lhf__bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
.lhf__bg::before,
.lhf__bg::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.07);
}
.lhf__bg::before {
  width: 360px;
  height: 360px;
  top: -120px;
  right: -100px;
}
.lhf__bg::after {
  width: 280px;
  height: 280px;
  bottom: -100px;
  left: -80px;
}

/* ── Logo (único elemento, móvil y desktop) ──────────────── */
.lhf__logo {
  align-self: flex-start;
  flex-shrink: 0;
  margin-bottom: 32px;
  z-index: 1;
  position: relative;
}
.lhf__logo img { width: 120px; height: auto; display: block; }

/* ── Contenido (texto + formulario) ──────────────────────── */
.lhf__content {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  text-align: center;
}

/* ── H1 bicolor ──────────────────────────────────────────── */
.lhf__title {
  font-size: clamp(32px, 8vw, 52px);
  font-weight: 700;
  color: #312A5E;
  line-height: 1.15;
  letter-spacing: -1.5px;
  margin-bottom: 16px;
}
.lhf__title em {
  font-style: normal;
  color: #fff;
}

/* ── Subtítulo ───────────────────────────────────────────── */
.lhf__subtitle {
  font-size: 17px;
  color: #312A5E;
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 28px;
}

/* ── Formulario ──────────────────────────────────────────── */
.lhf__form { margin-bottom: 16px; text-align: left; }
.lhf__form--compact { margin-bottom: 3px; }

.lhf__legal {
  font-size: 12px;
  color: #312A5E;
  opacity: 0.7;
  margin-top: 10px;
  line-height: 1.5;
}

/* ── Botón + social proof ────────────────────────────────── */
.lhf__actions { margin-bottom: 6px; text-align: left; }
.lhf__social-proof {
  font-size: 13px;
  color: #312A5E;
  opacity: 0.7;
  font-weight: 500;
  margin-top: 8px;
}

/* Recortar el espacio interno que añade LoginSocial */
.login-box__social { margin-top: 4px !important; text-align: left; }
.login-box__social ::v-deep .social-login__o span { background-color: #FFCC00; }

/* ── Cover: la imagen/card del hero ─────────────────────── */
.lhf__cover {
  width: 100%;
  margin-top: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.lhf__cover--bleed {
  width: 100vw;
  margin-left: -24px;
  margin-right: -24px;
  margin-bottom: -56px;
}

/* ── CTA sticky footer bar ───────────────────────────────── */
.lhf__sticky-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 1000;
  background: #312A5E;
  border-top: 2px solid #FFCC00;
  padding: 12px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-shadow: 0 -4px 24px rgba(49, 42, 94, 0.3);
  animation: lhf-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes lhf-slide-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
.lhf__sticky-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  margin: 0;
}
.lhf__sticky-btn {
  width: 100%;
  max-width: 340px;
  background: #FFCC00;
  border: 1px solid #FFCC00;
  border-radius: 50px;
  padding: 14px 24px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  color: #312A5E;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.15s ease;
}
.lhf__sticky-btn:hover { background: #f0c000; border-color: #f0c000; }
.lhf__sticky-btn:active { transform: scale(0.98); }

/* ── Transición password ─────────────────────────────────── */
.lhf-reveal-enter-active,
.lhf-reveal-leave-active {
  transition: max-height 0.4s ease, opacity 0.4s ease;
  overflow: hidden;
}
.lhf-reveal-enter,
.lhf-reveal-leave-to { max-height: 0; opacity: 0; }
.lhf-reveal-enter-to,
.lhf-reveal-leave { max-height: 200px; opacity: 1; }

/* ── DESKTOP: fiel al ejemplo ────────────────────────────── */
@media (min-width: 1024px) {
  .lhf {
    flex-direction: row;
    align-items: center;
    gap: 64px;
    padding: 80px 10%;
    min-height: 100svh;
  }

  .lhf__logo {
    align-self: flex-start;
    margin-bottom: 0;
  }
  .lhf__logo img { width: 140px; }

  .lhf__content {
    flex: 1;
    max-width: 480px;
    text-align: left;
  }

  .lhf__cover {
    flex-shrink: 0;
    margin-top: 0;
    width: auto;
  }

  .lhf__sticky-bar {
    flex-direction: row;
    justify-content: center;
    gap: 24px;
    padding: 14px 40px;
  }
  .lhf__sticky-btn { width: auto; padding: 12px 36px; }
}
</style>
